import { createUpdatableContext } from "fe-web-components/context/context";
import {
  Category,
  DiagnosisOverview,
  DiseaseCategoryType,
  MeasurementRange,
  PatientCaseGroupedFeature,
  PatientSuggestionsPerDiseaseMetrics,
  QualifiedFeature,
} from "fe-common/graphql";
import {
  createContext,
  Dispatch,
  useCallback,
  useEffect,
  useMemo,
} from "react";
import { GenderValue } from "shared";
import { DataInputPanel } from "../components/dataInputTypes";
import {
  Suggestion,
  SuggestionDiseaseStrength,
  UseSuggestions,
} from "../components/diagnosticAgent/hooks/useSuggestions";
import { getCurrentCase } from "../updateCase/updateCaseMgr";
import {
  UserStorageKeyType,
  useUserStorage,
  UseUserStorageApi,
} from "fe-web-components/userStorage/useUserStorage";
import { UserStorageKey } from "fe-web-components/userStorage/userStorageKey";
import { DDStatusData } from "../components/diagnosticAgent/updateDDToState";

export enum DiagnosticAgentType {
  Covid = "Covid",
  InternalMedicine = "InternalMedicine",
  ER = "ER",
  FirstAid = "FirstAid",
  FamilyMedicine = "FamilyMedicine",
  Test = "Test",
}

/**
 * The top-level sections of the case. For now these are the same as the top-level of panels,
 * but in the future they may diverge.
 */
export enum PatientCaseGrouping {
  CLINICAL_FEATURES = "clinicalFeatures",
  WORKUP = "workup",
  REVIEW_OF_SYSTEMS = "reviewOfSystems",
  BACKGROUND = "background",
  VITAL_SIGNS = "vitalSigns",
  MAIN = "main",
}

/**
 * Each step belongs to different navigation group
 */
export enum NavigationGroup {
  DIAGNOSIS = "diagnosis",
  PLAN = "plan",
  PATIENT = "patient",
}

/***
 * An attempt to make panels navigation clearer for components,
 * I've added a panel context under-which we can implement decision points,
 * specific ui requirements and navigation
 */
export interface PanelContext {
  /***
   *   Vital Sings / Background / Workup etc...
   */
  caseGrouping: PatientCaseGrouping;
  /***
   * hobbits, family history, Signs & symptoms, background, lab tests
   */
  panelCategory: Category;
  /***
   * Panels under the panel category - not always exits,
   * but if so will appear as chips with icons in the panel view
   */
  panel: DataInputPanel;

  /***
   *  Says if panel is used in search
   */
  fromSearch?: boolean;
}

export const panelContext2String = (context: PanelContext) => {
  const panelName = (context?.panel?.title || "").toLowerCase();
  const panelCategoryName = context?.panelCategory?.name?.toLowerCase();
  const skipPanelCategoryName =
    !panelCategoryName || panelCategoryName === panelName;

  return `${panelName}${skipPanelCategoryName ? "" : ` ${panelCategoryName} `}`;
};

export interface PatientCase
  extends Record<PatientCaseGrouping, QualifiedFeature[]> {
  gender: GenderValue | null;
  age: MeasurementRange | null;
  diseasesToDiscard: QualifiedFeature[];
  diseasesToAdd: QualifiedFeature[];
  panelsToAdd: QualifiedFeature[];
  diseasesToRemoveFromPlan: QualifiedFeature[];
  diseasesToIncludeInPlan: QualifiedFeature[];
  //Did not want to chagne the current PatientCase to have PatientCaseGroupedFeature instead of QualifiedFeature
  //Since it will require a lot of changes, so I add it here
  //The UI can still use QualifiedFeature as today, but when needed to store/other features requires the new format
  //It can use savedFindings
  savedGroupedFindings: Record<
    PatientCaseGrouping,
    PatientCaseGroupedFeature[]
  >;
}

export interface DiagnosticAgentState {
  patientCase?: PatientCase;
  pendingFindings?: PatientCase;
  diagnosis?: DiagnosisOverview;
  ddStatus?: DDStatusData;
  dirty: boolean; // whether the patient case has been changed without the diagnosis updating
  restoredCaseChanged: boolean; // whether the patient case chagned since it was loaded
  planDirty: boolean;
  lastPlanCategory: DiseaseCategoryType[];
  panels?: Map<string, DataInputPanel>;
  smartPanels?: Map<string, DataInputPanel>;
  restoredCaseID?: string;
  diagnosisLoading?: boolean;
  agentType: DiagnosticAgentType;
  isLocalRestored: boolean;
}

export const blankPatientCase = (): PatientCase =>
  Object.values(PatientCaseGrouping).reduce((o, p) => (o[p] = []) && o, {
    diseasesToRemoveFromPlan: [],
    diseasesToIncludeInPlan: [],
    panelsToAdd: [],
    diseasesToAdd: [],
    diseasesToDiscard: [],
    savedGroupedFindings: Object.values(PatientCaseGrouping).reduce(
      (o, p) => (o[p] = []),
      {},
    ),
  }) as PatientCase;

const isPatientCaseNotBlank = (patientCaseObj) =>
  Object.keys(patientCaseObj).some(
    (key) =>
      patientCaseObj[key] &&
      (!Array.isArray(patientCaseObj[key]) || patientCaseObj[key].length),
  );

export const shareIdQueryParams = ["shareid", "shareID", "shareId"];

const clinicPathParam = "clinic";

// Make sure to match clinic as a route and not to mix it with plain text in
// the path just having 'clinic' in it like /clinicalFeatures. Match either
// end of exp, /, ?, or plain text after /clinic
const clinicRegex = new RegExp(`/${clinicPathParam}[$,\\/,?,\\s]`);

export const useHasSharedId = (): boolean => {
  const userStorage = useUserStorage();
  return (
    shareIdQueryParams.some((qp) => window.location.search.includes(qp)) ||
    (clinicRegex.test(window.location.pathname) &&
      !!getCurrentCase(userStorage).caseId)
  );
};

export const ClinicModeContext = createContext(false);

const useInitialSessionState = () => {
  const userStorage = useUserStorage();
  const { localCase, hasLocalData } = getPatientCaseFromLocalStore(userStorage);
  return {
    patientCase: useHasSharedId()
      ? blankPatientCase()
      : { ...blankPatientCase(), ...localCase },
    pendingFindings: { ...blankPatientCase() },
    panels: null,
    smartPanels: null,
    agentType: DiagnosticAgentType.InternalMedicine,
    //getAgentTypeFromLocalStore() || DiagnosticAgentType.InternalMedicine,
    dirty: true,
    restoredCaseChanged: false,
    planDirty: true,
    lastPlanCategory: null,
    isLocalRestored: useHasSharedId() ? false : hasLocalData,
  };
};

export interface RefinePopupContextProps {
  open: boolean;
  refineQualifier?: QualifiedFeature;
  patientCaseGrouping?: PatientCaseGrouping;
  causedByDisease?: (PatientSuggestionsPerDiseaseMetrics &
    SuggestionDiseaseStrength)[];
}

export interface EditPanelPopupContextProps {
  open: boolean;
  patientCaseGrouping?: PatientCaseGrouping;
  suggestions?: Suggestion[];
  causedByDisease?: (PatientSuggestionsPerDiseaseMetrics &
    SuggestionDiseaseStrength)[];
  panelContext?: PanelContext;
}

export interface StartOverDialogProps {
  open: boolean;
  isLocalRestored?: boolean;
}

export interface PatientFirstSummaryDialogProps {
  open: boolean;
  isLocalRestored?: boolean;
}

export interface FeedBackDialogProps {
  open: boolean;
  caseId?: string;
  clinicName?: string;
}

export enum UserManagementPopupMode {
  Login = "Login",
  ResetPassword = "loginPopupState",
  SignUp = "SignUp",
  Closed = "Closed",
  Logout = "Logout", // Just to signal event listeners the state was changed
}

export type DiagnosticNavigation = {
  inWizard: boolean;
  expandedDiseases: { [id: string]: boolean };
  ddScrollLocation: any;
  menuOpen: boolean;
  refinePopupProps: RefinePopupContextProps;
  editPanelPopupProps: EditPanelPopupContextProps;
  startOverDialogProps: StartOverDialogProps;
  patientFirstSummaryDialogProps: PatientFirstSummaryDialogProps;
  feedbackDialogProps: FeedBackDialogProps;
  caseMismatchDialogOpen: boolean;
  addDiseaseOpen: boolean;
  improveDiagnosisPopupOpen: boolean;
  loginPopupState: UserManagementPopupMode;
  forceLogin: boolean;
  // A message to be displayed in cases where we request the user to log in for
  // certain purpose
  loginPopupMessage: string;
  nextRouteNavigationDisabled: boolean;
  showPwaInstallWindow: boolean;
  showIosInstallPwaMessage: boolean;
  toastOpen: boolean;
  toastText: string;
};

export const [
  ProviderDiagnosticAgentState,
  useDiagnosticAgentState,
] = createUpdatableContext<DiagnosticAgentState>(null, useInitialSessionState);

export const defaultNavigationState: DiagnosticNavigation = {
  expandedDiseases: {},
  inWizard: true,
  ddScrollLocation: null,
  menuOpen: false,
  startOverDialogProps: { open: false, isLocalRestored: false },
  patientFirstSummaryDialogProps: { open: false, isLocalRestored: false },
  caseMismatchDialogOpen: false,
  feedbackDialogProps: { open: false, caseId: null },
  refinePopupProps: null,
  editPanelPopupProps: null,
  addDiseaseOpen: false,
  loginPopupState: UserManagementPopupMode.Closed,
  loginPopupMessage: "",
  forceLogin: false,
  nextRouteNavigationDisabled: false,
  showPwaInstallWindow: false,
  showIosInstallPwaMessage: false,
  improveDiagnosisPopupOpen: false,
  toastOpen: false,
  toastText: "",
};

// Use this state to initialize values per-session regardless of the stored
// state from previous session
export const sessionResetNavigationState: Partial<DiagnosticNavigation> = {
  showPwaInstallWindow: false,
  showIosInstallPwaMessage: false,
  nextRouteNavigationDisabled: false,
  menuOpen: false,
  loginPopupState: UserManagementPopupMode.Closed,
  loginPopupMessage: "",
  improveDiagnosisPopupOpen: false,
  // override local storage since we want it to be always false on load
  toastOpen: false,
  toastText: "",
  caseMismatchDialogOpen: false,
};

const useInitialNavigationState = () => {
  return {
    ...defaultNavigationState,
    ...getNavigationStateFromLocalStore(useUserStorage()),
    ...sessionResetNavigationState,
  };
};

export const [
  ProviderDiagnosticAgentNavigation,
  useDiagnosticAgentNavigationState,
] = createUpdatableContext<DiagnosticNavigation>(
  null,
  useInitialNavigationState,
);

export const defaultSuggestionsState: UseSuggestions = {
  findingSuggestions: [],
  suggestionDebug: null,
  planIsLoading: false,
  clusteredSuggestions: [],
};

export const [
  ProviderSuggestionsState,
  useSuggestionsState,
] = createUpdatableContext<UseSuggestions>(defaultSuggestionsState);

export const [
  ProviderDDRequired,
  useDiagnosticAgentDDRequired,
] = createUpdatableContext<boolean>(true);

export const useDiagnosticAgentType = (): [
  DiagnosticAgentType,
  Dispatch<DiagnosticAgentType>,
] => {
  const [state, set] = useDiagnosticAgentState();

  const updateAgentType = useCallback(
    (agentType: DiagnosticAgentType) =>
      set((oldState) => ({ ...oldState, agentType })),
    [set],
  );

  return useMemo(() => [state?.agentType, updateAgentType], [
    state,
    updateAgentType,
  ]);
};

/**
 * reset agent type in an effect, safe to call from render function
 */
export function useResetDiagnosticAgentType(agentType: DiagnosticAgentType) {
  const [at, setAt] = useDiagnosticAgentType();
  useEffect(() => {
    if (agentType && at !== agentType) {
      setAt(agentType);
      console.log("Resetting Agent Type to", agentType);
    }
  }, [setAt, at, agentType]);
  return at;
}

//export const getAgentRoute = () => `/agent`;

function cleanPanelsToAdd(
  patientCase: PatientCase,
  panels: QualifiedFeature[],
) {
  return (
    panels &&
    panels.filter((addedPanel) =>
      patientCase.workup.some(
        (qf) => qf.feature.id === addedPanel.qualifiers[0].value.id,
      ),
    )
  );
}
export function cleanPatientCaseForSave(patientCase: PatientCase) {
  const clonedPatientCase = JSON.parse(JSON.stringify(patientCase));
  // Filter panels which have no findings in patient case
  clonedPatientCase.panelsToAdd = cleanPanelsToAdd(
    patientCase,
    patientCase.panelsToAdd,
  );
  clonedPatientCase.savedGroupedFindings = patientCase.savedGroupedFindings;

  return clonedPatientCase;
}

export function setPatientCaseToLocalStore(
  agent: DiagnosticAgentType,
  patientCase: PatientCase,
  userStorage: UseUserStorageApi,
) {
  const cleanedPatientCase = cleanPatientCaseForSave(patientCase);

  cleanedPatientCase &&
    userStorage.writeToStorage(
      UserStorageKey.PatientCase,
      JSON.stringify(cleanedPatientCase),
      UserStorageKeyType.Session,
    );

  agent && setAgentToLocalStore(agent, userStorage);
}
export function setAgentToLocalStore(
  agent: DiagnosticAgentType,
  userStorage: UseUserStorageApi,
) {
  agent && userStorage.writeToStorage(UserStorageKey.AgentType, agent);
}

export function getPatientCaseFromLocalStore(userStorage: UseUserStorageApi) {
  const localCase = userStorage.readFromStorage<PatientCase>(
    UserStorageKey.PatientCase,
  );
  const hasLocalData = localCase && isPatientCaseNotBlank(localCase);
  return { localCase, hasLocalData };
}

export function getAgentTypeFromLocalStore(userStorage: UseUserStorageApi) {
  return userStorage.readFromStorage<DiagnosticAgentType>(
    UserStorageKey.AgentType,
  );
}

export function setNavigationStateToLocalStore(
  navigationState: DiagnosticNavigation,
  userStorage: UseUserStorageApi,
) {
  navigationState &&
    userStorage.writeToStorage(
      UserStorageKey.NavigationState,
      JSON.stringify(navigationState),
    );
}

export function getNavigationStateFromLocalStore(
  userStorage: UseUserStorageApi,
): Partial<DiagnosticNavigation> {
  return userStorage.readFromStorage(UserStorageKey.NavigationState);
}
